import React, { useState, useRef, useContext, useEffect } from "react";
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

import SigninForm from "../../components/Forms/SigninForm/SigninForm";

import {
    validateDoc,
    validateName,
    validateEmail,
    validatePhone,
    validatePassword,
    validateConfirmPassword,
} from '../../validation/index';

import useRememberMe from "../../hooks/useRememberMe";
import useSetPageTitle from "../../hooks/useSetPageTitle";
import UserContext from "../../contexts/UserContext";

import './styles.css';
import useDataLayer from "hooks/useDataLayer";

function Signin() {
    const navigate = useNavigate();
    const dataLayer = useDataLayer();

    const { setCurrentUser, currentPromo } = useContext(UserContext);

    useEffect(() => {
        if (currentPromo.status !== 'active') {
            navigate(`/${currentPromo.path}/login`);
        }
    }, [currentPromo, navigate])

    useRememberMe(currentPromo);
    useSetPageTitle('Cadastro', currentPromo);

    const docRef = useRef();
    const nameRef = useRef();
    const emailRef = useRef();
    const phoneRef = useRef();
    const passwordRef = useRef();
    const confirmPasswordRef = useRef();

    const [doc, setDoc] = useState('');
    const [docWarning, setDocWarning] = useState('');

    const [name, setName] = useState('');
    const [nameWarning, setNameWarning] = useState('');

    const [email, setEmail] = useState('');
    const [emailWarning, setEmailWarning] = useState('');

    const [phone, setPhone] = useState('');
    const [phoneWarning, setPhoneWarning] = useState('');

    const [password, setPassword] = useState('');
    const [passwordWarning, setPasswordWarning] = useState('');

    const [confirmPassword, setConfirmPassword] = useState('');
    const [confirmPasswordWarning, setConfirmPasswordWarning] = useState('');

    const [agreeWithRegulation, setAgreeWithRegulation] = useState(false);
    const [agreeWithRegulationWarning, setAgreeWithRegulationWarning] = useState('');
    const [agreeWithSharing, setAgreeWithSharing] = useState(false);
    const [agreeWithSharingWarning, setAgreeWithSharingWarning] = useState('');

    const [loading, setLoading] = useState(false);
    const [signinError, setSigninError] = useState(null); // Adicione o estado da mensagem de erro

    const scrollToTop = () => window.scrollTo({ top: 120, behavior: 'smooth' });
    const scrollToDoc = () => window.scrollTo({ behavior: 'smooth', top: docRef.current.offsetTop + 120 });
    const scrollToName = () => window.scrollTo({ behavior: 'smooth', top: nameRef.current.offsetTop + 120 });
    const scrollToEmail = () => window.scrollTo({ behavior: 'smooth', top: emailRef.current.offsetTop + 120 });
    const scrollToPhone = () => window.scrollTo({ behavior: 'smooth', top: phoneRef.current.offsetTop + 120 });
    const scrollToPassword = () => window.scrollTo({ behavior: 'smooth', top: passwordRef.current.offsetTop + 120 });
    const scrollToConfirmPassword = () => window.scrollTo({ behavior: 'smooth', top: confirmPasswordRef.current.offsetTop + 120 });

    const handleDoc = (inputedDoc) => validateDoc(inputedDoc, setDoc, setDocWarning);
    const handleName = (inputedName) => validateName(inputedName, setName, setNameWarning);
    const handleEmail = (inputedEmail) => validateEmail(inputedEmail, setEmail, setEmailWarning);
    const handlePassword = (inputedPassword) => validatePassword(inputedPassword, setPassword, setPasswordWarning);
    const handlePhone = (inputedPhone) => validatePhone(inputedPhone, setPhone, setPhoneWarning);
    const handleConfirmPassword = (inputedPassword) => validateConfirmPassword(inputedPassword, setConfirmPassword, setConfirmPasswordWarning, password);

    const trySigninIn = (e) => {
        setSigninError(null); 
        e.preventDefault();
        if (!doc.length) setDocWarning('Este é um campo obrigatório');
        if (!name.length) setNameWarning('Este é um campo obrigatório');
        if (!email.length) setEmailWarning('Este é um campo obrigatório');
        if (!phone.length) setPhoneWarning('Este é um campo obrigatório');
        if (!password.length) setPasswordWarning('Este é um campo obrigatório');
        if (!confirmPassword.length) setConfirmPasswordWarning('Este é um campo obrigatório');
        if (!agreeWithRegulation) setAgreeWithRegulationWarning('Você precisa aceitar o regulamento');
        if (!agreeWithSharing) setAgreeWithSharingWarning('Você precisa aceitar este item');
        if (!doc.length || docWarning.length) {
            scrollToDoc();
            return;
        }
        if (!name.length || nameWarning.length) {
            scrollToName();
            return;
        }
        if (!email.length || emailWarning.length) {
            scrollToEmail();
            return;
        }
        if (!phone.length || phoneWarning.length) {
            scrollToPhone();
            return;
        }
        if (!password.length || passwordWarning.length) {
            scrollToPassword();
            return;
        }
        if (!confirmPassword.length || confirmPasswordWarning.length) {
            scrollToConfirmPassword();
            return;
        }
        if (!agreeWithRegulation || !agreeWithSharing) {
            return;
        }
        setLoading(true);
        axios.post(`${process.env.REACT_APP_API_URL}/client/register`, {
                doc: doc,
                email: email,
                password: password,
                nome: name,
                telefone: phone,
                promo_id: currentPromo.id
            })
            .then((response) => {
                setLoading(false);
                if (response.status === 200) {
                    setCurrentUser(response.data.client, response.data.accessToken)
                    localStorage.setItem('firstTime', 'true');
                    dataLayer.push({
                        event: 'cadastro-ok',
                    });
                    navigate(`/${currentPromo.path}/home?success=true`);
                }
            })
            .catch((err) => {
                setLoading(false);
                dataLayer.push({
                    event: 'error_event',
                    error_code: err.response.status,
                    error_message: err.response.data.message
                });
                setSigninError({ title: `Erro ao efetuar cadastro - ${err.response.status}`, subtitle: err.response.data.message })
                scrollToTop();
                return;
            });
    }

    return (
        <div className="signin-page-container">
            <SigninForm
                doc={doc}
                setDoc={handleDoc}
                docWarning={docWarning}
                docRef={docRef}
                name={name}
                setName={handleName}
                nameWarning={nameWarning}
                nameRef={nameRef}
                email={email}
                setEmail={handleEmail}
                emailWarning={emailWarning}
                emailRef={emailRef}
                phone={phone}
                setPhone={handlePhone}
                phoneWarning={phoneWarning}
                phoneRef={phoneRef}
                password={password}
                setPassword={handlePassword}
                passwordWarning={passwordWarning}
                passwordRef={passwordRef}
                confirmPassword={confirmPassword}
                setConfirmPassword={handleConfirmPassword}
                confirmPasswordWarning={confirmPasswordWarning}
                confirmPasswordRef={confirmPasswordRef}
                onClick={trySigninIn}
                agreeWithRegulation={agreeWithRegulation}
                setAgreeWithRegulation={setAgreeWithRegulation}
                agreeWithRegulationWarning={agreeWithRegulationWarning}
                setAgreeWithRegulationWarning={setAgreeWithRegulationWarning}
                agreeWithSharing={agreeWithSharing}
                setAgreeWithSharing={setAgreeWithSharing}
                agreeWithSharingWarning={agreeWithSharingWarning}
                setAgreeWithSharingWarning={setAgreeWithSharingWarning}
                haveAnAccount={() => navigate(`/${currentPromo.path}/login`)}
                loading={loading}
                signinError={signinError}
            />
        </div>
    );
}

export default Signin;
